import classnames from 'classnames/bind';
import debounce from 'lodash.debounce';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';
import throttle from 'lodash/throttle';

import AttachmentUploadButton from 'presentation/selfService/AttachmentUploadButton';
import { ERRORS } from 'enums/ui';
import InputBox from 'presentation/components/InputBox';
import ImagePreview from 'presentation/selfService/ImagePreview';
import { isMobileIos } from 'lib/getCustomerProperties';
import overrideStyle from 'lib/overrideStyle';
import SubmitButtonContainer from 'presentation/containers/SubmitButtonContainer';
import { TypingState } from 'enums/session';

import styles from 'presentation/components/styles/Composition.css';
let cx = classnames.bind(styles);

// Typing event is throttled to 1/5sec, stop typing event debounced every start typing call
// Stop typing timeout must be at least 500ms longer to allow time for next typing update
const START_TYPING_INTERVAL = 5000;
const STOP_TYPING_TIMEOUT = 5500;
const MAX_ATTACHMENT_SIZE = 10000000; // 10mb to match smooch

class InputValidationComposition extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldShowError: false,
    };

    this.handleAttachmentChange = this.handleAttachmentChange.bind(this);
    this.handleCloseImage = this.handleCloseImage.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.isMobileIos = isMobileIos();
    this.addTouchHandler = this.addTouchHandler.bind(this);
    this.removeTouchHandler = this.removeTouchHandler.bind(this);
    this.handleTouchEvent = this.handleTouchEvent.bind(this);
    this.enableTouchScrolling = this.enableTouchScrolling.bind(this);
    this.disableTouchScrolling = this.disableTouchScrolling.bind(this);
    this.focusInput = this.focusInput.bind(this);
    this.removeFocus = this.removeFocus.bind(this);
    this.preventScrollingFn = this.preventScrollingFn.bind(this);

    this.inputRef = React.createRef();
    this.compositionRef = React.createRef();
  }

  componentDidMount() {
    this.isMobileIos ? this.addTouchHandler() : null;
    this.props.shouldAutoFocus ? this.focusInput() : null;
  }

  componentWillMount() {
    this.handleActiveTyping = throttle(this.handleTypingStart, START_TYPING_INTERVAL, { trailing: false });
    this.handleTypingTimeout = debounce(this.handleTypingStop, STOP_TYPING_TIMEOUT);
  }

  componentWillUnmount() {
    if (this.isMobileIos) {
      this.removeTouchHandler();
      this.enableTouchScrolling();
    }
  }

  focusInput() {
    this.inputRef.current && this.inputRef.current.focus();
  }

  handleTouchEvent(ev) {
    /* Do not remove focus and close the virtual keyboard when touching something inside the composition */
    if (this.compositionRef.current && this.compositionRef.current.contains(ev.target)) {
      return;
    }
    /* We need to remove focus from the composition on web mobile iOS devices when touching outside the composition to close the keyboard. Mobile iOS does not resize the screen when the keyboard opens so the messages at the top of the chat window won't be visible while the keyboard is open. */
    this.removeFocus();
  }

  addTouchHandler() {
    window.addEventListener('touchstart', this.handleTouchEvent);
  }

  removeTouchHandler() {
    window.removeEventListener('touchstart', this.handleTouchEvent);
  }

  preventScrollingFn(ev) {
    let inputBox = get(this.inputRef, 'current.textAreaRef.current');
    if (inputBox && inputBox.contains(ev.target)) {
      // Allow scrolling within the input box
      return;
    }
    // Prevent the entire Sidekick from being scrolled when the keyboard is open
    ev.preventDefault();
  }

  enableTouchScrolling() {
    this.compositionRef.current.removeEventListener('touchmove', this.preventScrollingFn, { passive: false });
  }

  disableTouchScrolling() {
    this.compositionRef.current.addEventListener('touchmove', this.preventScrollingFn, { passive: false });
  }

  removeFocus() {
    document.activeElement.blur();
  }

  handleAttachmentChange(ev) {
    if (ev.target.files) {
      let files = ev.target.files;
      let newAttachments = [];

      // TODO: Only allowing one attachment per composition until we can find away around smooch's webhook issue
      //  we are running into issues where the webhook is being overloaded with requests and not all of the attachments
      //  are being given the `message:sent` event that lets us present the attachment at the right time

      // for (let i = 0; i < files.length; i++) {
      //   files[i] &&
      //     newAttachments.push({ blob: URL.createObjectURL(files[i]), fileType: files[i].type, file: files[i] });
      // }

      if (files[0] && files[0].size < MAX_ATTACHMENT_SIZE) {
        newAttachments.push({ blob: URL.createObjectURL(files[0]), fileType: files[0].type, file: files[0] });

        if (this.props.attachments) {
          newAttachments = this.props.attachments.concat(newAttachments);
        }
        this.props.onAttachmentChange(newAttachments);
      } else {
        this.props.onAttachmentError(ERRORS.FILE_SIZE);
      }
      this.focusInput();
    }
  }

  handleCloseImage(index) {
    let newAttachments = [...this.props.attachments];
    newAttachments.splice(index, 1);
    this.props.onAttachmentChange(newAttachments, this.props.compositionDraft);
  }

  handleInputChange(ev) {
    this.props.onChange(ev.target.value, this.props.attachments);
    this.handleActiveTyping();
  }

  handleKeyDown(ev) {
    let keyCode = ev.key;
    const shouldSend = keyCode === 13 || keyCode === 'Enter';

    if (shouldSend && !ev.shiftKey) {
      this.handleSubmit();
      ev.stopPropagation();
      ev.preventDefault();
      return false;
    }
  }

  handleSubmit() {
    this.handleTypingStop();
    if (this.props.isChatClientConnected && this.props.hasContent) {
      const currentInput = this.props.compositionDraft.trim();
      if (this.props.onValidate(currentInput)) {
        this.props.onSubmit(currentInput, this.props.attachments);
        this.setState({ shouldShowError: false });
      } else {
        this.setState({ shouldShowError: true });
      }
      this.focusInput();
    }
  }

  handleTypingStart() {
    this.props.onCustomerTypingStateUpdate && this.props.onCustomerTypingStateUpdate(TypingState.STARTED);
    this.handleTypingTimeout();
  }

  handleTypingStop() {
    this.props.onCustomerTypingStateUpdate && this.props.onCustomerTypingStateUpdate(TypingState.STOPPED);
  }

  renderAttachments() {
    return this.props.attachments.map((attachment, index) => (
      <ImagePreview
        id="imagePlaceholder"
        fileType={attachment.fileType}
        fileName={attachment.file.name}
        key={index}
        number={index}
        onCloseImage={this.handleCloseImage}
        src={attachment.blob}
      />
    ));
  }

  renderAttachmentButton() {
    if (this.props.canAddAttachments) {
      return (
        <AttachmentUploadButton
          hasContent={this.props.hasContent}
          role="button"
          onChange={this.handleAttachmentChange}
          onKeyDown={() => true}
          title="gladlyAttachmentInputButton"
        />
      );
    }
  }

  renderSubmitButton() {
    if (this.props.hasContent) {
      return (
        <SubmitButtonContainer
          aria-label="submitButton"
          canAddAttachments={this.props.canAddAttachments}
          hasContent={this.props.hasContent}
          role="button"
          onClick={this.handleSubmit}
          onKeyDown={this.handleKeyDown}
          data-aid="gladlyChatCompositionSubmit"
        />
      );
    }
  }

  render() {
    // classname only applies when there there is content in the inputBox
    let hasAttachments = this.props.attachments && this.props.attachments[0];
    let compositionInputBoxClasses = cx(styles.composition_inputBox_withAttachments);
    let compositionClassnames = cx(styles.composition, { composition_selfService: this.props.isSelfService });
    return (
      <div className={compositionClassnames} ref={this.compositionRef}>
        <InputFlexWrapper>
          {this.state.shouldShowError ? (
            <CompositionError id="gladlyCompositionError" role="alert">
              {this.props.errorMessage}
            </CompositionError>
          ) : null}
          <InnerInputFlexWrapper hasAttachments={hasAttachments}>
            <pre className={styles.composition_spacer_withAttachments}>
              {this.props.compositionDraft}
              <br />
            </pre>
            <InputBox
              className={compositionInputBoxClasses}
              data-aid="gladlyChatCompositionInput"
              data-gramm_editor="false"
              onBlur={this.isMobileIos ? this.enableTouchScrolling : null}
              onChange={this.handleInputChange}
              onFocus={this.isMobileIos ? this.disableTouchScrolling : null}
              onKeyDown={this.handleKeyDown}
              hasAttachments={hasAttachments}
              attachmentsEnabled={this.props.attachmentsEnabled}
              prompt={this.props.prompt}
              ref={this.inputRef}
              role="form"
              value={this.props.compositionDraft}
            />
            {this.renderAttachmentButton()}
            {this.renderSubmitButton()}
          </InnerInputFlexWrapper>
          <PreviewWrapper hasAttachments={hasAttachments}>
            {hasAttachments ? this.renderAttachments() : null}
          </PreviewWrapper>
        </InputFlexWrapper>
      </div>
    );
  }
}

InputValidationComposition.propTypes = {
  canAddAttachments: PropTypes.bool,
  compositionDraft: PropTypes.string,
  errorMessage: PropTypes.string,
  isChatClientConnected: PropTypes.bool.isRequired,
  hasContent: PropTypes.bool.isRequired,
  onAttachmentChange: PropTypes.func,
  onAttachmentError: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onCustomerTypingStateUpdate: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onValidate: PropTypes.func,
  prompt: PropTypes.string.isRequired,
  shouldAutoFocus: PropTypes.bool,
};

const CompositionError = overrideStyle('span')`
  color: #D2220E;
  font-family: ${p => p.theme.fontLibrary.secondary};
  font-size: 11px;
  left: 20px;
  position: absolute;
  top: 10px;
  z-index: 1;
`;

const InputFlexWrapper = overrideStyle('span')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InnerInputFlexWrapper = overrideStyle('span')`
  display: flex;
  flex-direction: row;
  max-height: ${p => (p.hasAttachments ? '100px' : 'none')};
`;

const PreviewWrapper = overrideStyle('span')`
  display: flex;
  flex-direction: row;
  overflow: auto;
  padding: ${p => (p.hasAttachments ? `4px 0px 16px 8px` : '0px')};
  width: 80%;
`;

export default InputValidationComposition;
